import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { FREE_MODE } from '../config/appConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getWsUrl = () => {
  const url = new URL(BACKEND_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};

const ChatContext = createContext(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const audioRef = useRef(null);

  // Initialize notification sound
  useEffect(() => {
    // Create audio element for notification sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp2fkZCQlpuZl5GOkI2PjpCSlJaXl5WSkI6Ni4uNjpCSlZaXl5WSkI6MioqMjpCSlZaXl5WSkI6MioqMjY+Rk5WWlpSSj42LioqLjY+Rk5WWlpSSj42LioqLjY+RlJaWlpSSj42LioqLjY+RlJaWlpSSj42Ki4uLjY+RlJaWlZORj42Li4uLjY+RlJWVlZORj42Li4uLjY+RlJWVlJKQjoyLi4uNj5GTlZWUko+NjIuLi42PkZOVlZSSj42Mi4uLjY+Rk5WVlJKPjYyLi4uNj5GTlZSUko+NjIuLi42PkZOVlJSSj42Mi4uLjY+Rk5SUkpCOjYyLi4uNj5GTlJSSkI6NjIuLi42PkZOUlJKQjo2Mi4uLjY+Rk5SUkpCOjYyLi4uNj5GUlJSSkI6NjIyLjY+RlJSUkpCOjY2MjI2Pj5GUlJOSkI6NjYyMjY+PkZSUk5KQjo2NjIyNj4+RlJSTkpCOjY2MjI2Pj5GUlJOSkI6NjYyMjY+PkZSUk5KQjo2NjIyNj4+RlJSTkpCOjY2MjI2Pj5GUlJOSkI6NjYyMjY+PkZSUk5KQjo2NjIyNj4+RlJSTkpCOjY2MjI2Pj5GUlJOSkI6NjYyMjY+PkZSUk5KQjo2NjI2Nj4+RlJSTko+OjY2MjI2Pj5GUk5KQjo6NjYyNjY+RlJOSkI6OjY2MjY2PkZSTko+Ojo2NjI2Nj5GUk5KPjo6NjYyNjY+RlJOSj46OjY2MjY2PkZSTko+Ojo2NjI2Nj5GUk5KPjo6NjY2NjY+RlJKSj46OjY2NjY2PkZSSko+Ojo2NjY2Nj5GUkpKPjo6NjY2NjY+RlJKSj46OjY2NjY2PkZSSko+Ojo2NjY2Nj5GUkpKPjo6NjY2NjY+RlJKSj46OjY2NjY2PkZSSko+Ojo2NjY2Nj5GUkpKPjo6OjY2NjY+RlJKSj46Ojo2NjY2PkZSSko+Ojo6NjY2Nj5GSkpKPjo6OjY2NjY+RkpKSj46Ojo2NjY2PkZKSko+Ojo6NjY2Nj5GSkpKPjo6OjY2NjY+RkpKSj46Ojo6NjY2PkZKSkpCOjo6OjY2Nj5GSkpKQjo6OjY2NjY+RkpKSkI6Ojo2NjY2PkZKSkpCOjo6NjY2Nj5GSkpKQjo6OjY2NjY+RkpKSkI6Ojo2NjY2PkZKSkpCOjo6OjY2Nj5GSkpKQjo6OjY2NjY+RkpKSkI6Ojo6NjY2PkZKSkpCOjo6OjY2Nj5GSkpKQjo6OjY2NjY+RkpKSkI6Ojo6NjY2PkZKSkpCOjo6OjY2Nj5GSkpKQjo6Ojo2NjY+RkpKSkI6Ojo6NjY2PkZKSkpCOjo6OjY2Nj5GSkpKQjo6Ojo2NjY+R');
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, []);

  // Fetch unread count via REST API
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setUnreadCount(0);
      return;
    }

    try {
      const res = await fetch(`${API}/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [isAuthenticated, token]);

  // Handle incoming WebSocket messages for global notifications
  const handleGlobalMessage = useCallback((data) => {
    if (data.type === 'new_message' && data.message?.sender_id !== user?.user_id) {
      // New message from someone else - increment unread count
      setUnreadCount(prev => prev + 1);
      playNotificationSound();
    } else if (data.type === 'messages_read') {
      // Messages were read - refresh count
      fetchUnreadCount();
    }
  }, [user, playNotificationSound, fetchUnreadCount]);

  // Connect to WebSocket for global notifications
  useEffect(() => {
    if (!isAuthenticated || !token || !user?.user_id) {
      setWsConnected(false);
      return;
    }

    // Don't connect if company without subscription (only in paid mode)
    if (!FREE_MODE && user.user_type === 'company' && !user.subscription_active) {
      return;
    }

    const connect = () => {
      const wsUrl = `${getWsUrl()}/api/ws/chat/${user.user_id}?token=${token}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onopen = () => {
          setWsConnected(true);
          fetchUnreadCount();
        };
        
        wsRef.current.onclose = () => {
          setWsConnected(false);
          // Reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        };
        
        wsRef.current.onerror = () => {
          setWsConnected(false);
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleGlobalMessage(data);
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, token, user, handleGlobalMessage, fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const value = {
    unreadCount,
    setUnreadCount,
    wsConnected,
    fetchUnreadCount,
    playNotificationSound,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
